<script setup lang="ts">
import { isEqual } from 'lodash';
import { computed, ref } from 'vue';
import IconCheck from './assets/check.svg';

const props = withDefaults(defineProps<{
  modelValue?: boolean | any[];
  value?: any;
  disabled?: boolean;
  hint?: string;
  error?: string;
  dataTestpl?: string;
}>(), {
  modelValue: false,
  value: null,
  disabled: false,
  hint: '',
  error: '',
  dataTestpl: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: any];
}>();

const model = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const isFocused = ref(false);

const isChecked = computed(() => {
  return Array.isArray(model.value)
    ? model.value.some((item) => isEqual(item, props.value))
    : model.value;
});

const appearanceClass = computed(() => {
  if (props.disabled) {
    return 'opacity-50 outline-transparent';
  }
  if (props.error) {
    return 'outline-error-500';
  }
  if (isFocused.value && isChecked.value) {
    return 'outline-primary-200';
  }
  if (isFocused.value) {
    return 'outline-primary-500';
  }
  return 'outline-transparent';
});
</script>

<template>
  <div>
    <label
      class="flex items-center gap-14"
      :class="{
        'cursor-pointer': !disabled,
      }"
    >
      <input
        v-model="model"
        class="pointer-events-none absolute opacity-0"
        :value="value"
        type="checkbox"
        :disabled="disabled"
        :data-testpl="dataTestpl"
        @focus="isFocused = true"
        @blur="isFocused = false"
      >

      <span
        class="flex
            h-[22px]
            w-[22px]
            min-w-[22px]
            items-center
            justify-center
            rounded-[5px]
            bg-primary-200
            outline
            outline-2
            transition-all duration-200"
        :class="[
          appearanceClass,
          {
            'bg-primary-500': isChecked,
            'bg-primary-200': !isChecked,
          }
        ]"
      >
        <IconCheck
          class="text-white transition-opacity duration-200"
          :class="{
            'opacity-0': !isChecked,
            'opacity-100': isChecked,
          }"
        />
      </span>

      <span class="text-12">
        <slot />
      </span>
    </label>

    <p
      v-if="error"
      class="mt-8 text-14 text-error-500"
    >
      {{ error }}
    </p>

    <p
      v-else-if="hint"
      class="mt-8 text-14 text-deep-blue-100"
    >
      {{ hint }}
    </p>
  </div>
</template>
